import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigationType } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import DOMPurify from "dompurify";
import { KineticText } from "../components/KineticText";
import { Reveal } from "../components/Reveal";
import { ProjectCard } from "../components/ProjectCard";
import { Footer } from "../components/Footer";
import { Spinner } from "../components/Spinner";
import { fetchProjects, fetchSiteContent, sizedUrl, srcSetFor } from "../lib/api";
import { scrollKey } from "../components/SmoothScroll";
import { useLang } from "../context/LanguageContext";

const Hero = ({ images, interval, line1, line2, tagline, countLabel }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const slides = images && images.length ? images : [];
  const [idx, setIdx] = useState(0);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => { if (window.scrollY > 8) setScrolled(true); };
    window.addEventListener("scroll", onScroll, { passive: true });
    const lenis = window.__lenis;
    if (lenis) lenis.on("scroll", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (lenis) lenis.off("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    if (slides.length <= 1) { setIdx(0); return; }
    const ms = Math.max(1, Number(interval) || 5) * 1000;
    const id = setTimeout(() => setIdx((i) => (i + 1) % slides.length), ms);
    return () => clearTimeout(id);
  }, [idx, slides.length, interval]);

  const advance = () => { if (slides.length > 1) setIdx((i) => (i + 1) % slides.length); };

  // Only keep the slides already shown (plus the next one) in the DOM, so the
  // browser doesn't download every hero image up front — matters on mobile data.
  const mounted = useMemo(() => {
    const set = new Set([0]);
    for (let i = 0; i <= idx; i++) set.add(i);
    if (slides.length > 1) set.add((idx + 1) % slides.length);
    return set;
  }, [idx, slides.length]);

  return (
    <section ref={ref} data-testid="hero" onClick={advance} className="relative h-screen w-full overflow-hidden">
      <motion.div style={{ y: yImg, scale: scaleImg }} className="absolute inset-0" data-testid="hero-image-layer">
        {slides.map((s, i) => (
          mounted.has(i) ? (
            <img key={s.url} src={sizedUrl(s.url, 1920)} srcSet={srcSetFor(s.url, [480, 800, 1200, 1920])} sizes="100vw" alt="VESSEL"
              fetchPriority={i === 0 ? "high" : "auto"}
              style={{ objectPosition: s.position || "center", opacity: slides.length <= 1 ? 1 : (i === idx ? 1 : 0), transition: "opacity 0.8s ease-in-out" }}
              className="absolute inset-0 w-full h-full object-cover" />
          ) : null
        ))}
        <div className="absolute inset-0 bg-[var(--vsl-bg)]/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--vsl-bg)] via-transparent to-[var(--vsl-bg)]/40" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative h-full mx-auto max-w-[1600px] px-6 md:px-12 flex flex-col justify-end pb-[8vh]">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 1 }}
          className="font-mono text-[11px] tracking-[0.4em] uppercase text-[var(--vsl-limestone)] mb-6"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(tagline || "", { ADD_ATTR: ["target", "rel"] }) }} />
        <div data-testid="hero-lockup" className="max-w-[16ch] text-[var(--vsl-on-media)]">
          <motion.span initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="block font-barlow font-medium uppercase text-[15vw] md:text-[9vw] leading-[0.82] tracking-[0.005em]">
            Shaping
          </motion.span>
          <motion.span initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.58, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="block font-display uppercase text-[12vw] md:text-[7vw] leading-[0.95] tracking-[0.04em] mt-1">
            Objects
          </motion.span>
        </div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 1 }}
          className="mt-10 flex items-center gap-4 font-mono text-[11px] tracking-[0.2em] uppercase text-[var(--vsl-muted)]">
          <span className="w-10 h-px bg-[var(--vsl-muted)]" />
          <motion.span data-testid="scroll-cue"
            animate={scrolled ? { opacity: 1 } : { opacity: [1, 0.2, 1] }}
            transition={scrolled ? { duration: 0.6 } : { duration: 2.6, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}>
            {countLabel}
          </motion.span>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default function Home() {
  const { t, L, tc } = useLang();
  const navType = useNavigationType();
  const [projects, setProjects] = useState([]);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  // capture saved scroll at first render, before any scroll event can overwrite it
  const savedScrollRef = useRef(
    (() => { try { return parseInt(sessionStorage.getItem(scrollKey("/")) || "0", 10) || 0; } catch (e) { return 0; } })()
  );

  useEffect(() => {
    Promise.all([fetchProjects(), fetchSiteContent()])
      .then(([p, c]) => { setProjects(p); setContent(c); })
      .catch((e) => console.error("home data failed:", e))
      .finally(() => setLoading(false));
  }, []);

  // Restore scroll position when arriving via browser back/forward.
  useEffect(() => {
    if (loading || navType !== "POP") return;
    const y = savedScrollRef.current;
    if (!y || y <= 0) return;
    let r1, r2, t1, t2;
    const apply = () => {
      if (window.__lenis) { window.__lenis.resize(); window.__lenis.scrollTo(y, { immediate: true }); }
      window.scrollTo(0, y);
    };
    r1 = requestAnimationFrame(() => { r2 = requestAnimationFrame(apply); });
    t1 = setTimeout(apply, 180);
    t2 = setTimeout(apply, 450);
    return () => { cancelAnimationFrame(r1); cancelAnimationFrame(r2); clearTimeout(t1); clearTimeout(t2); };
  }, [loading, navType]);

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(projects.flatMap((p) => p.categories || [p.category]).filter(Boolean)))],
    [projects]
  );
  const filtered = filter === "All"
    ? projects
    : projects.filter((p) => (p.categories || [p.category]).includes(filter));

  if (loading) return <Spinner />;

  const heroImages = (content?.hero_images && content.hero_images.length)
    ? content.hero_images
    : (content?.hero_image
        ? [{ url: content.hero_image, position: content.hero_position || "center" }]
        : (projects[0]?.cover ? [{ url: projects[0].cover, position: projects[0].cover_position || "center" }] : []));

  return (
    <main data-testid="home-page">
      <Hero
        images={heroImages}
        interval={content?.hero_interval || 5}
        line1={L(content, "hero_line1") || "Shaping raw stone"}
        line2={L(content, "hero_line2") || "into shaped objects."}
        tagline={L(content, "hero_tagline")}
        countLabel={t("scroll_explore")}
      />

      <section data-testid="projects-section" className="mx-auto max-w-[1600px] px-6 md:px-12 pt-28 md:pt-40 pb-10">
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
          <div>
            <p className="font-mono text-[11px] tracking-[0.4em] uppercase text-[var(--vsl-green)] mb-4">{t("home_index")}</p>
            <h2 className="font-display text-6xl md:text-8xl leading-[0.9]">{t("home_selected")}</h2>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 font-mono text-[11px] tracking-[0.2em] uppercase">
            {categories.map((c) => (
              <button key={c} data-testid={`filter-${c.toLowerCase()}`} onClick={() => setFilter(c)}
                className={`nav-link transition-colors ${filter === c ? "text-[var(--vsl-fg)] active" : "text-[var(--vsl-muted)] hover:text-[var(--vsl-fg)]"}`}>
                {c === "All" ? t("filter_all") : tc(c)}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 items-start"
          onPointerDownCapture={() => { try { sessionStorage.setItem(scrollKey("/"), String(Math.round(window.scrollY))); } catch (e) { console.warn("scroll position not saved:", e); } }}>
          {filtered.map((p, i) => (
            <div key={p.id} className="col-span-1">
              <ProjectCard project={p} aspect="aspect-square" index={i} />
            </div>
          ))}
        </motion.div>
      </section>

      <Footer content={content} />
    </main>
  );
}
